import * as React from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Breadcrumbs,
  Container,
  CssBaseline,
  Toolbar,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  Chip,
} from "@mui/material";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import Footer from "../components/Footer";
import Navigation from "../components/Navigation";
import SquarespacePageHeader from "../components/SquarespacePageHeader";
import { Link } from "react-router-dom";
import { useApi } from "../hooks/useApi";

export default function SquarespaceOrderDetail() {
  const { id } = useParams();
  const api = useApi();
  const [order, setOrder] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    const fetchOrder = async () => {
      setLoading(true);
      setError(null);
      try {
        const responseData = await api.get(`/admin/squarespace/orders/${id}`);
        setOrder(responseData.data || null);
      } catch (err) {
        console.error("Error fetching order:", err);
        setError(err.message || "Failed to fetch order");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchOrder();
    }
  }, [id, api]);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return "N/A";
    try {
      return `$${Number(amount).toFixed(2)}`;
    } catch {
      return amount;
    }
  };

  const getStatusColor = (status) => {
    if (!status) return "default";
    const statusLower = status.toLowerCase();
    switch (statusLower) {
      case "fulfilled":
      case "completed":
        return "success";
      case "shipped":
        return "info";
      case "pending":
      case "processing":
        return "warning";
      case "cancelled":
      case "canceled":
        return "error";
      default:
        return "default";
    }
  };

  const orderNumber = order?.order_number || order?.orderNumber || order?.id || id;

  return (
    <>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />
        <Navigation />
        <Box
          component="main"
          sx={{
            backgroundColor: (theme) => theme.palette.background.default,
            flexGrow: 1,
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            overflow: "auto",
          }}
        >
          <Toolbar />
          <Container maxWidth="lg" sx={{ mt: 2, mb: 4, flexGrow: 1 }}>
            <Box sx={{ mb: 2 }}>
              <Breadcrumbs aria-label="breadcrumb">
                <Typography
                  color="primary"
                  component={Link}
                  to="/dashboard"
                  sx={{
                    textDecoration: "none",
                    cursor: "pointer",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Home
                </Typography>
                <Typography
                  color="primary"
                  component={Link}
                  to="/dashboard/squarespace"
                  sx={{
                    textDecoration: "none",
                    cursor: "pointer",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Squarespace
                </Typography>
                <Typography
                  color="primary"
                  component={Link}
                  to="/dashboard/squarespace/orders"
                  sx={{
                    textDecoration: "none",
                    cursor: "pointer",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Orders
                </Typography>
                <Typography color="text.primary" fontWeight={500}>
                  {orderNumber}
                </Typography>
              </Breadcrumbs>
            </Box>

            <SquarespacePageHeader
              title="Order Details"
              description="View Squarespace order information"
              icon={<ShoppingCartIcon sx={{ color: "primary.main" }} />}
            />

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress />
              </Box>
            ) : order ? (
              <Card variant="outlined">
                <CardContent sx={{ pt: 2, pb: 2 }}>
                  <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ mb: 1.5 }}>
                    Order Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                        Order Number
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.25 }} fontFamily="monospace">
                        {order.order_number || order.orderNumber || order.id || "N/A"}
                      </Typography>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                        Order ID
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.25 }} fontFamily="monospace">
                        {order.id || "N/A"}
                      </Typography>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                        Status
                      </Typography>
                      <Box sx={{ mt: 0.25 }}>
                        <Chip
                          label={order.status ? (order.status.charAt(0).toUpperCase() + order.status.slice(1)) : "N/A"}
                          color={getStatusColor(order.status)}
                          size="small"
                        />
                      </Box>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                        Total Amount
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.25 }}>
                        {formatCurrency(order.total || order.totalAmount)}
                      </Typography>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                        Number of Items
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.25 }}>
                        {Array.isArray(order.items) ? order.items.length : (order.itemCount ?? 0)}
                      </Typography>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                        Customer Name
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.25 }}>
                        {order.customer_name || order.customerName || "N/A"}
                      </Typography>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                        Customer Email
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.25 }}>
                        {order.customer_email || order.customerEmail || "N/A"}
                      </Typography>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                        Created At
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.25 }}>
                        {formatDate(order.created_at || order.createdAt)}
                      </Typography>
                    </Grid>

                    {Object.entries(order).map(([key, value]) => {
                      // Skip already displayed fields
                      const displayedFields = ['id', 'order_number', 'orderNumber', 'status', 'total', 'totalAmount', 'items', 'itemCount', 'customer_name', 'customerName', 'customer_email', 'customerEmail', 'created_at', 'createdAt'];
                      if (displayedFields.includes(key)) return null;
                      if (value === null || value === undefined) return null;
                      
                      return (
                        <Grid key={key} size={{ xs: 12, sm: 6, md: 4 }}>
                          <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                            {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.25 }}>
                            {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                          </Typography>
                        </Grid>
                      );
                    })}

                    <Grid size={{ xs: 12 }}>
                      <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: "divider" }}>
                        <Typography variant="h6" gutterBottom fontWeight="bold">
                          Full Order Data
                        </Typography>
                        <Box
                          component="pre"
                          sx={{
                            p: 2,
                            bgcolor: "grey.50",
                            borderRadius: 1,
                            overflow: "auto",
                            fontSize: "0.875rem",
                            fontFamily: "monospace",
                          }}
                        >
                          {JSON.stringify(order, null, 2)}
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            ) : (
              <Alert severity="warning">
                Order not found
              </Alert>
            )}
          </Container>
          <Footer />
        </Box>
      </Box>
    </>
  );
}
